import { Skeleton } from '@mantine/core';
import type { ColumnDef, Row, RowSelectionState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionsCell } from '../components/actions-cell';
import { CheckboxCell, CheckboxHeaderCell } from '../components/checkbox-cell';
import type { TableAction, TableColumn } from '../types';

export const useTableColumns = <T,>(
  columns: TableColumn<T>[],
  actions?: TableAction<T>[],
  isLoading?: boolean,
  selectedRows?: RowSelectionState,
  rowSelectionDisabledTooltip?: string | ((row: Row<T>) => string | undefined)
): ColumnDef<T>[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    const checkboxColumn: TableColumn<T> = {
      id: 'checkbox',
      header: CheckboxHeaderCell<T>,
      cell: ({ row }) => (
        <CheckboxCell row={row} disabledTooltip={rowSelectionDisabledTooltip} />
      ),
      meta: { hug: true, preventRowClick: true },
    };

    const actionsColumn: TableColumn<T> = {
      id: 'actions',
      header: t('words.actions', { defaultValue: 'Actions' }),
      cell: ({ row }) => <ActionsCell actions={actions} row={row} />,
      meta: { hug: true, preventRowClick: true },
    };

    const allColumns = [
      ...conditionalColumn<T>(checkboxColumn, !!selectedRows),
      ...columns,
      ...conditionalColumn<T>(actionsColumn, !!actions),
    ];

    if (isLoading) {
      return allColumns.map((column) => ({
        ...column,
        cell: () => <Skeleton height={14} />,
      }));
    }

    return allColumns;
  }, [
    actions,
    columns,
    isLoading,
    rowSelectionDisabledTooltip,
    selectedRows,
    t,
  ]);
};

const conditionalColumn = <T,>(column: TableColumn<T>, shouldShow: boolean) => {
  return shouldShow ? [column] : [];
};
